// OrganizationForm.tsx - Updated: authorized is now per-region + Help Panel
import React, { useState, useEffect } from 'react';
import './CompanyManagement.css';
import './CompanyFormOverrides.css';
import { ApiService } from './apiService';
import { 
  Organization, 
  OrganizationFormData, 
  Region, 
  CompanyRegion,
  OrganizationManagementProps 
} from './types';

interface OrganizationFormProps extends OrganizationManagementProps {
  editingOrganization: Organization | null;
  regions: Region[];
  isLoadingRegions: boolean;
  onBack: () => void;
  onSuccess: () => void;
}

// Extended region interface for form usage with fileId and authorized
interface SelectedRegion extends Omit<Region, 'regionName' | 'regionCode'> {
  regionName: string;
  regionCode: string;
  fileId?: string;
  authorized: boolean; // NEW: per-region authorization
}

// ─── Help Panel Content ─────────────────────────────────────────────
interface HelpItem {
  title: string;
  description: string;
  tip?: string;
}

const HELP_CONTENT: Record<string, HelpItem> = {
  default: {
    title: 'Company Registration',
    description: 'Use this form to register a new company or update an existing company\'s details in the gate pass system. Please verify that the company is legally registered and authorised to use the facility before adding it to the system.',
    tip: 'All fields marked with * are required. Click on any field to see specific guidance.'
  },
  companyName: {
    title: 'Company Name',
    description: 'Enter the full legal name of the company as it appears on official documents and contracts.',
    tip: 'This name will appear on all gate passes issued for this company.'
  },
  contactEmail: {
    title: 'Contact Email',
    description: 'Provide the primary contact email for the company. This will be used for all official communications and notifications.',
    tip: 'Use a company email address (e.g. admin@company.com) rather than a personal one.'
  },
  contactNumber: {
    title: 'Contact Number',
    description: 'Enter the main telephone number for the company\'s point of contact.',
    tip: 'Include the country code for international numbers (e.g. +974 for Qatar).'
  },
  url: {
    title: 'Company URL',
    description: 'Provide the company\'s official website address for verification purposes.',
    tip: 'Enter the full URL including the domain (e.g. www.company.com).'
  },
  description: {
    title: 'Description',
    description: 'Add a brief description of the company, its core business activities, or any relevant notes.',
    tip: 'This is optional but helps administrators identify the company quickly.'
  },
  regions: {
    title: 'Regions',
    description: 'Assign the company to one or more operational regions. Each region requires a unique File ID for document tracking.',
    tip: 'A company must be assigned to at least one region before it can be used in gate pass requests.'
  },
  fileId: {
    title: 'Region File ID',
    description: 'The File ID is a unique document reference number assigned to the company for a specific region.',
    tip: 'This is typically provided by the regional office or contract administrator.'
  },
  authorized: {
    title: 'Authorized Status',
    description: 'Controls whether this company is authorized to operate in the selected region. Un-authorized companies cannot submit new gate pass requests for that region.',
    tip: 'You can temporarily de-authorize a company for a region without removing the region assignment.'
  },
  blacklisted: {
    title: 'Blacklisted',
    description: 'Marking a company as blacklisted will prevent it from submitting any new gate pass requests across all regions.',
    tip: 'Use this for companies that have been suspended or banned from operations.'
  }
};

export const OrganizationForm: React.FC<OrganizationFormProps> = ({
  editingOrganization,
  regions,
  isLoadingRegions,
  getAccessToken,
  onStatusUpdate,
  onBack,
  onSuccess
}) => {
  const [formData, setFormData] = useState<OrganizationFormData>({
    companyName: '',
    blacklisted: false,
    description: '',
    contactNumber: '',
    url: '',
    contactEmail: '',
    regions: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRegionId, setSelectedRegionId] = useState<string>('');
  const [selectedRegions, setSelectedRegions] = useState<SelectedRegion[]>([]);
  const [apiService] = useState(() => new ApiService(getAccessToken));
  const [focusedField, setFocusedField] = useState<string>('default');
  const [showHelp, setShowHelp] = useState<boolean>(true);

  // Initialize form data when editing
  useEffect(() => {
    if (editingOrganization) {
      setFormData({
        companyName: editingOrganization.companyName,
        blacklisted: editingOrganization.blacklisted,
        description: editingOrganization.description,
        contactNumber: editingOrganization.contactNumber,
        url: editingOrganization.url,
        contactEmail: editingOrganization.contactEmail,
        regions: editingOrganization.regions || []
      });
      const validRegions: SelectedRegion[] = (editingOrganization.regions || [])
        .filter((region): region is CompanyRegion & { regionName: string; regionCode: string } => 
          region.regionName != null && region.regionCode != null
        )
        .map(region => ({
          ...region,
          regionName: region.regionName,
          regionCode: region.regionCode,
          fileId: region.fileId || '',
          authorized: region.authorized !== undefined ? region.authorized : true
        }));
      
      setSelectedRegions(validRegions);
    }
  }, [editingOrganization]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Sync selectedRegions to formData.regions
  const syncRegionsToFormData = (updatedRegions: SelectedRegion[]) => {
    setSelectedRegions(updatedRegions);
    const companyRegions: CompanyRegion[] = updatedRegions.map(r => ({
      regionId: r.regionId,
      regionName: r.regionName,
      regionCode: r.regionCode,
      fileId: r.fileId,
      authorized: r.authorized,
      isActive: r.isActive,
      description: r.description
    }));
    setFormData(prev => ({
      ...prev,
      regions: companyRegions
    }));
  };

  // Add region to company
  const handleAddRegion = () => {
    if (!selectedRegionId) return;
    
    const regionToAdd = regions.find(r => r.regionId.toString() === selectedRegionId);
    if (!regionToAdd) return;

    // Check if region is already added
    const isAlreadyAdded = selectedRegions.some(r => r.regionId === regionToAdd.regionId);
    if (isAlreadyAdded) {
      onStatusUpdate({
        type: 'error',
        message: 'Region is already added to this company'
      });
      return;
    }

    const newRegion: SelectedRegion = {
      ...regionToAdd,
      regionName: regionToAdd.regionName!,
      regionCode: regionToAdd.regionCode!,
      fileId: '', // Initialize empty file ID
      authorized: true // Default new regions to authorized
    };

    syncRegionsToFormData([...selectedRegions, newRegion]);
    setSelectedRegionId(''); // Reset dropdown
  };

  // Remove region from company
  const handleRemoveRegion = (regionId: number) => {
    const updatedRegions = selectedRegions.filter(r => r.regionId !== regionId);
    syncRegionsToFormData(updatedRegions);
  };

  // Update file ID for a specific region
  const handleFileIdChange = (regionId: number, fileId: string) => {
    const updatedRegions = selectedRegions.map(region => 
      region.regionId === regionId 
        ? { ...region, fileId }
        : region
    );
    syncRegionsToFormData(updatedRegions);
  };

  // Toggle authorized for a specific region
  const handleAuthorizedChange = (regionId: number, authorized: boolean) => {
    const updatedRegions = selectedRegions.map(region =>
      region.regionId === regionId
        ? { ...region, authorized }
        : region
    );
    syncRegionsToFormData(updatedRegions);
  };

  // Get available regions for dropdown (exclude already selected)
  const getAvailableRegions = () => {
    return regions.filter(region => 
      region.regionName && region.regionCode && // Ensure required fields exist
      !selectedRegions.some(selected => selected.regionId === region.regionId)
    );
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that at least one region is selected
    if (selectedRegions.length === 0) {
      onStatusUpdate({
        type: 'error',
        message: 'Please select at least one region for the company'
      });
      return;
    }

    // Validate that all selected regions have a File ID
    const regionsWithoutFileId = selectedRegions.filter(r => !r.fileId || r.fileId.trim() === '');
    if (regionsWithoutFileId.length > 0) {
      const regionNames = regionsWithoutFileId.map(r => r.regionName).join(', ');
      onStatusUpdate({
        type: 'error',
        message: `File ID is required for all regions. Please provide a File ID for: ${regionNames}`
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let result;
      if (editingOrganization) {
        result = await apiService.updateOrganization(editingOrganization.companyId, formData);
      } else {
        result = await apiService.createOrganization(formData);
      }

      if (result.success) {
        onStatusUpdate({
          type: 'success',
          message: result.message || 'Operation completed successfully'
        });
        onSuccess();
      } else {
        onStatusUpdate({
          type: 'error',
          message: result.message || 'Operation failed'
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      onStatusUpdate({
        type: 'error',
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setFormData({
      companyName: '',
      blacklisted: false,
      description: '',
      contactNumber: '',
      url: '',
      contactEmail: '',
      regions: []
    });
    setSelectedRegions([]);
    setSelectedRegionId('');
    onBack();
  };

  // Get current help content based on focused field
  const currentHelp = HELP_CONTENT[focusedField] || HELP_CONTENT.default;

  return (
    <div className="modal-overlay">
      <div className={`modal-container ${showHelp ? 'company-form-with-help' : ''}`}>
        <div className="modal-header">
          <h2 className="modal-title">{editingOrganization ? 'Edit Company' : 'Create Company'}</h2>
          <button 
            type="button" 
            className="modal-close-btn"
            onClick={handleClose}
            aria-label="Close modal"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="modal-content-with-help">
          {/* ─── Left: Form ─────────────────────────────────────────── */}
          <form onSubmit={handleSubmit} className="modal-form company-form-main">
            <div className="modal-body">
              <div className="form-field" onFocus={() => setFocusedField('companyName')}>
                <label htmlFor="companyName" className="field-label">
                  Company Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="e.g. Apple INC"
                  className="field-input"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-field" onFocus={() => setFocusedField('contactEmail')}>
                <label htmlFor="contactEmail" className="field-label">
                  Contact Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  placeholder="e.g. info@apple.com"
                  className="field-input"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-field" onFocus={() => setFocusedField('contactNumber')}>
                <label htmlFor="contactNumber" className="field-label">
                  Contact Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  placeholder="e.g. 9089 111 111"
                  className="field-input"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-field" onFocus={() => setFocusedField('url')}>
                <label htmlFor="url" className="field-label">
                  Company URL <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="url"
                  name="url"
                  value={formData.url}
                  onChange={handleInputChange}
                  placeholder="e.g. www.apple.com"
                  className="field-input"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-field" onFocus={() => setFocusedField('description')}>
                <label htmlFor="description" className="field-label">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="e.g. Type here"
                  className="field-textarea"
                  rows={4}
                  disabled={isSubmitting}
                />
              </div>

              {/* Multiple Regions Section */}
              <div className="form-field" onFocus={() => setFocusedField('regions')} onClick={() => setFocusedField('regions')}>
                <label className="field-label">
                  Regions <span className="required">*</span>
                </label>
                
                {/* Add Region Controls */}
                <div className="region-add-controls">
                  <div className="region-selector">
                    <select
                      value={selectedRegionId}
                      onChange={(e) => setSelectedRegionId(e.target.value)}
                      className="field-select"
                      disabled={isSubmitting || isLoadingRegions}
                    >
                      <option value="">Select a region to add</option>
                      {getAvailableRegions().map((region) => (
                        <option key={region.regionId} value={region.regionId.toString()}>
                          {region.regionName} ({region.regionCode})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddRegion}
                      disabled={!selectedRegionId || isSubmitting}
                      className="btn-add-region"
                    >
                      Add Region
                    </button>
                  </div>
                </div>

                {/* Selected Regions List */}
                {selectedRegions.length > 0 && (
                  <div className="selected-regions-list">
                    <h4 className="regions-list-title">Selected Regions ({selectedRegions.length})</h4>
                    {selectedRegions.map((region) => (
                      <div key={region.regionId} className="region-item">
                        <div className="region-info">
                          <span className="region-name">{region.regionName || 'Unknown Region'}</span>
                          <span className="region-code">({region.regionCode || 'N/A'})</span>
                        </div>
                        
                        <div className="region-file-id" onFocus={() => setFocusedField('fileId')}>
                          <input
                            type="text"
                            placeholder="File ID (required)"
                            value={region.fileId || ''}
                            onChange={(e) => handleFileIdChange(region.regionId, e.target.value)}
                            className="file-id-input"
                            disabled={isSubmitting}
                            required
                          />
                        </div>

                        {/* Per-region authorized toggle */}
                        <div className="region-authorized-toggle" onClick={() => setFocusedField('authorized')}>
                          <label className="checkbox-label region-auth-label">
                            <input
                              type="checkbox"
                              checked={region.authorized}
                              onChange={(e) => handleAuthorizedChange(region.regionId, e.target.checked)}
                              className="field-checkbox"
                              disabled={isSubmitting}
                            />
                            <span className={`region-auth-text ${region.authorized ? 'auth-yes' : 'auth-no'}`}>
                              {region.authorized ? 'Authorized' : 'Not Auth.'}
                            </span>
                          </label>
                        </div>
                        
                        <button
                          type="button"
                          onClick={() => handleRemoveRegion(region.regionId)}
                          className="btn-remove-region"
                          disabled={isSubmitting}
                          title="Remove region"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {selectedRegions.length === 0 && (
                  <div className="no-regions-selected">
                    <p>No regions selected. Please add at least one region.</p>
                  </div>
                )}
              </div>

              {/* Blacklisted checkbox — only when editing */}
              <div className="form-checkboxes" onClick={() => setFocusedField('blacklisted')}>
                {editingOrganization && (
                  <div className="checkbox-field">
                    <input
                      type="checkbox"
                      id="blacklisted"
                      name="blacklisted"
                      checked={formData.blacklisted}
                      onChange={handleInputChange}
                      className="field-checkbox"
                      disabled={isSubmitting}
                    />
                    <label htmlFor="blacklisted" className="checkbox-label">
                      Blacklisted Organization
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              {/* Validation hints */}
              {!isSubmitting && selectedRegions.length === 0 && (
                <span className="footer-validation-hint">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  At least one region must be assigned.
                </span>
              )}
              {!isSubmitting && selectedRegions.length > 0 && selectedRegions.some(r => !r.fileId || r.fileId.trim() === '') && (
                <span className="footer-validation-hint">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  All regions require a File ID.
                </span>
              )}
              <div className="modal-footer-buttons">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={isSubmitting || selectedRegions.length === 0 || selectedRegions.some(r => !r.fileId || r.fileId.trim() === '')}
                >
                  {isSubmitting ? 'Saving...' : (editingOrganization ? 'Update Company' : 'Create Company')}
                </button>
              </div>
            </div>
          </form>

          {/* ─── Help Panel + Toggle (collapsible with animation) ───── */}

          {/* Panel — always rendered, animated via class */}
          <div className={`company-help-panel ${showHelp ? 'help-panel-open' : 'help-panel-closed'}`}>
            <div className="help-panel-inner">
              <div className={`help-panel-card ${focusedField !== 'default' ? 'help-active' : ''}`}>
                <h4 className="help-card-title">{currentHelp.title}</h4>
                <p className="help-card-description">{currentHelp.description}</p>
                {currentHelp.tip && (
                  <div className="help-card-tip">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    <span>{currentHelp.tip}</span>
                  </div>
                )}
              </div>

              {/* Quick reference — always visible */}
              <div className="help-panel-quickref">
                <h4 className="help-quickref-title">Quick Reference</h4>
                <ul className="help-quickref-list">
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Verify the company is legally registered to use the facility</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Fields marked <span style={{ color: '#F3694A', fontWeight: 600 }}>*</span> are required</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Each region needs a unique File ID</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>At least one region must be assigned</span>
                  </li>
                  <li>
                    <span className="help-quickref-bullet"></span>
                    <span>Authorization can be toggled per region</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Toggle strip — always visible, outer (right) edge */}
          <div className="help-toggle-strip">
            <button
              type="button"
              className="help-toggle-icon-btn"
              onClick={() => setShowHelp(!showHelp)}
              title={showHelp ? 'Hide Guide' : 'Show Guide'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </button>
            <button
              type="button"
              className="help-toggle-tab"
              onClick={() => setShowHelp(!showHelp)}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {showHelp
                  ? <polyline points="9 18 15 12 9 6"/>
                  : <polyline points="15 18 9 12 15 6"/>
                }
              </svg>
              <span>{showHelp ? 'Hide Guide' : 'Show Guide'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};